/**
 * Layout Component
 * 
 * Main application layout with professional sidebar navigation.
 * Enterprise-grade design with refined typography and spacing.
 */

import { useState, useRef, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import {
  LayoutDashboard,
  Cpu,
  LogOut,
  User,
  Users,
  Key,
  Cloud,
  Server,
  Network,
  ChevronRight,
  ChevronDown,
  Settings,
  HelpCircle,
  ExternalLink,
  Activity,
  Code2,
  DollarSign,
  Wallet,
  Menu,
  X,
} from 'lucide-react';
import { useAuthStore } from '../stores/auth';
import { profileApi, operationsApi } from '../lib/api';
import clsx from 'clsx';
import OperationsPanel from './OperationsPanel';

const navItems = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/jobs', icon: Cpu, label: 'GPU Jobs' },
  { to: '/serverless', icon: Cloud, label: 'Serverless' },
  { to: '/infrastructure', icon: Server, label: 'Infrastructure' },
  { to: '/network', icon: Network, label: 'Network Setup' },
  { to: '/operations', icon: Activity, label: 'Operations' },
  { to: '/finance', icon: DollarSign, label: 'Finance' },
  { to: '/payouts', icon: Wallet, label: 'Payouts' },
];

export default function Layout() {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isOperationsPanelOpen, setIsOperationsPanelOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);

  // Fetch user profile to get avatar
  const { data: profile } = useQuery({
    queryKey: ['user-profile'],
    queryFn: profileApi.getProfile,
    enabled: !!user,
    staleTime: 30000,
  });

  // Query for active operations count
  const { data: activeOperations = [] } = useQuery({
    queryKey: ['operations-active'],
    queryFn: operationsApi.listActive,
    refetchInterval: 5000,
    enabled: !!user,
  });

  const activeOperationsCount = activeOperations.filter(op => 
    ['running', 'queued', 'blocked'].includes(op.status)
  ).length;

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Close profile dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Generate initials from email
  const getInitials = (email: string | undefined) => {
    if (!email) return 'U';
    const parts = email.split('@')[0].split(/[._-]/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return email.substring(0, 2).toUpperCase();
  };

  // Generate a consistent color based on email
  const getAvatarColor = (email: string | undefined) => {
    if (!email) return 'from-surface-600 to-surface-700';
    const colors = [
      'from-blue-500 to-indigo-600',
      'from-emerald-500 to-teal-600',
      'from-violet-500 to-purple-600',
      'from-rose-500 to-pink-600',
      'from-amber-500 to-orange-600',
      'from-cyan-500 to-blue-600',
    ];
    const hash = email.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  const sidebarContent = (
    <>
      {/* Logo */}
      <div className="p-5 border-b border-surface-800/50">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center overflow-hidden">
            <img src="/TY.png" alt="Tyaus Logo" className="w-full h-full object-contain" />
          </div>
          <div className="flex-1">
            <h1 className="font-semibold text-base text-white tracking-tight">Tyaus</h1>
            <p className="text-xs text-surface-500">On-Demand Computing</p>
          </div>
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            className="md:hidden p-1.5 text-surface-400 hover:text-surface-200 rounded-lg hover:bg-surface-800/50 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Operations Button */}
      <div className="px-3 pt-3 pb-2">
        <button
          onClick={() => {
            setIsOperationsPanelOpen(true);
            setIsMobileMenuOpen(false);
          }}
          className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-surface-800/50 transition relative group"
        >
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-surface-400 group-hover:text-surface-300" />
            <span className="text-sm text-surface-300 group-hover:text-surface-200">Operations</span>
          </div>
          {activeOperationsCount > 0 && (
            <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-blue-500 text-white text-xs font-bold rounded-full">
              {activeOperationsCount}
            </span>
          )}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 overflow-y-auto">
        <div className="space-y-1">
          {navItems.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              onClick={() => setIsMobileMenuOpen(false)}
              className={({ isActive }) =>
                clsx(
                  'group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200',
                  isActive
                    ? 'bg-gpu-500/15 text-gpu-400 border border-gpu-500/20'
                    : 'text-surface-400 hover:bg-surface-800/60 hover:text-surface-200 border border-transparent',
                )
              }
            >
              {({ isActive }) => (
                <>
                  <div className={clsx(
                    'w-8 h-8 rounded-lg flex items-center justify-center transition-colors',
                    isActive ? 'bg-gpu-500/20' : 'bg-surface-800/50 group-hover:bg-surface-800'
                  )}>
                    <Icon className={clsx('w-4 h-4', isActive ? 'text-gpu-400' : 'text-surface-400 group-hover:text-surface-300')} />
                  </div>
                  <span className="flex-1">{label}</span>
                  {isActive && (
                    <ChevronRight className="w-4 h-4 text-gpu-500/60" />
                  )}
                </>
              )}
            </NavLink>
          ))}

          {/* Admin Section - Only visible to admins */}
          {user?.role === 'admin' && (
            <>
              <div className="my-4 border-t border-surface-800/50" />
              <NavLink
                to="/admin/users"
                onClick={() => setIsMobileMenuOpen(false)}
                className={({ isActive }) =>
                  clsx(
                    'group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200',
                    isActive
                      ? 'bg-gpu-500/15 text-gpu-400 border border-gpu-500/20'
                      : 'text-surface-400 hover:bg-surface-800/60 hover:text-surface-200 border border-transparent',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <div className={clsx(
                      'w-8 h-8 rounded-lg flex items-center justify-center transition-colors',
                      isActive ? 'bg-gpu-500/20' : 'bg-surface-800/50 group-hover:bg-surface-800'
                    )}>
                      <Users className={clsx('w-4 h-4', isActive ? 'text-gpu-400' : 'text-surface-400 group-hover:text-surface-300')} />
                    </div>
                    <span className="flex-1">User Management</span>
                    {isActive && (
                      <ChevronRight className="w-4 h-4 text-gpu-500/60" />
                    )}
                  </>
                )}
              </NavLink>
              <NavLink
                to="/admin/endpoints"
                onClick={() => setIsMobileMenuOpen(false)}
                className={({ isActive }) =>
                  clsx(
                    'group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200',
                    isActive
                      ? 'bg-gpu-500/15 text-gpu-400 border border-gpu-500/20'
                      : 'text-surface-400 hover:bg-surface-800/60 hover:text-surface-200 border border-transparent',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <div className={clsx(
                      'w-8 h-8 rounded-lg flex items-center justify-center transition-colors',
                      isActive ? 'bg-gpu-500/20' : 'bg-surface-800/50 group-hover:bg-surface-800'
                    )}>
                      <Code2 className={clsx('w-4 h-4', isActive ? 'text-gpu-400' : 'text-surface-400 group-hover:text-surface-300')} />
                    </div>
                    <span className="flex-1">API Endpoints</span>
                    {isActive && (
                      <ChevronRight className="w-4 h-4 text-gpu-500/60" />
                    )}
                  </>
                )}
              </NavLink>
            </>
          )}
        </div>
      </nav>

      {/* Profile Section */}
      <div className="p-3 border-t border-surface-800/50" ref={profileRef}>
        {/* Profile Button */}
        <button
          onClick={() => setIsProfileOpen(!isProfileOpen)}
          className={clsx(
            'w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-200',
            isProfileOpen
              ? 'bg-surface-800/60 ring-1 ring-surface-700/50'
              : 'bg-surface-800/30 hover:bg-surface-800/50'
          )}
        >
          {profile?.avatarUrl ? (
            <img
              src={profile.avatarUrl}
              alt="Profile"
              className="w-10 h-10 rounded-xl object-cover shadow-lg border border-surface-700"
            />
          ) : (
            <div className={clsx(
              'w-10 h-10 rounded-xl bg-gradient-to-br flex items-center justify-center text-white font-semibold text-sm shadow-lg',
              getAvatarColor(user?.email)
            )}>
              {getInitials(user?.email)}
            </div>
          )}
          <div className="flex-1 min-w-0 text-left">
            <p className="text-sm font-medium text-surface-200 truncate">
              {user?.email?.split('@')[0] || 'User'}
            </p>
            <p className="text-xs text-surface-500 capitalize">{user?.role || 'Member'}</p>
          </div>
          <ChevronDown
            className={clsx(
              'w-4 h-4 text-surface-500 transition-transform duration-200',
              isProfileOpen && 'rotate-180'
            )}
          />
        </button>

        {/* Profile Dropdown */}
        <AnimatePresence>
          {isProfileOpen && (
            <motion.div
              initial={{ opacity: 0, y: -8, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.96 }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
              className="mt-2 bg-surface-900 border border-surface-800/60 rounded-xl shadow-2xl shadow-black/30 overflow-hidden"
            >
              <div className="p-4 border-b border-surface-800/50 bg-surface-800/30">
                <div className="flex items-center gap-3">
                  {profile?.avatarUrl ? (
                    <img
                      src={profile.avatarUrl}
                      alt="Profile"
                      className="w-12 h-12 rounded-xl object-cover shadow-lg border border-surface-700"
                    />
                  ) : (
                    <div className={clsx(
                      'w-12 h-12 rounded-xl bg-gradient-to-br flex items-center justify-center text-white font-semibold shadow-lg',
                      getAvatarColor(user?.email)
                    )}>
                      {getInitials(user?.email)}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white truncate">
                      {user?.email?.split('@')[0] || 'User'}
                    </p>
                    <p className="text-xs text-surface-400 truncate">
                      {user?.email}
                    </p>
                    <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded-md text-xs font-medium bg-gpu-500/15 text-gpu-400 border border-gpu-500/20 capitalize">
                      {user?.role || 'Member'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="p-2">
                <Link
                  to="/profile"
                  onClick={() => { setIsProfileOpen(false); setIsMobileMenuOpen(false); }}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-surface-300 hover:bg-slate-800/25 hover:text-surface-100 transition-colors"
                >
                  <User className="w-4 h-4 text-surface-500" />
                  <span>Profile Details</span>
                </Link>

                <Link
                  to="/api-keys"
                  onClick={() => { setIsProfileOpen(false); setIsMobileMenuOpen(false); }}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-surface-300 hover:bg-slate-800/25 hover:text-surface-100 transition-colors"
                >
                  <Key className="w-4 h-4 text-surface-500" />
                  <span>API Keys</span>
                  <span className="ml-auto text-xs text-surface-600">
                    <ExternalLink className="w-3 h-3" />
                  </span>
                </Link>

                <Link
                  to="/settings"
                  onClick={() => { setIsProfileOpen(false); setIsMobileMenuOpen(false); }}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-surface-300 hover:bg-slate-800/25 hover:text-surface-100 transition-colors"
                >
                  <Settings className="w-4 h-4 text-surface-500" />
                  <span>Settings</span>
                </Link>

                <div className="my-2 border-t border-surface-800/50" />

                <Link
                  to="/help"
                  onClick={() => { setIsProfileOpen(false); setIsMobileMenuOpen(false); }}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-surface-300 hover:bg-slate-800/25 hover:text-surface-100 transition-colors"
                >
                  <HelpCircle className="w-4 h-4 text-surface-500" />
                  <span>Help & Support</span>
                </Link>

                <div className="my-2 border-t border-surface-800/50" />

                <button
                  onClick={() => {
                    setIsProfileOpen(false);
                    setIsMobileMenuOpen(false);
                    handleLogout();
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-red-400 hover:bg-red-500/10 transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Log Out</span>
                </button>
              </div>

              <div className="px-4 py-3 border-t border-surface-800/50 bg-surface-800/20">
                <p className="text-xs text-surface-600 text-center">
                  Tyaus v1.0.0
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );

  return (
    <div className="h-screen bg-surface-950 flex flex-col md:flex-row overflow-hidden">
      {/* Desktop Sidebar */}
      <motion.aside
        initial={{ x: -20, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        className="hidden md:flex w-64 h-screen bg-surface-900/50 border-r border-surface-800/60 flex-col backdrop-blur-xl flex-shrink-0 overflow-hidden"
      >
        {sidebarContent}
      </motion.aside>

      {/* Mobile Sidebar Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <div className="fixed inset-0 z-50 md:hidden">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <motion.aside
              initial={{ x: -256 }}
              animate={{ x: 0 }}
              exit={{ x: -256 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
              className="fixed inset-y-0 left-0 w-64 bg-surface-900 border-r border-surface-800/60 flex flex-col z-50"
            >
              {sidebarContent}
            </motion.aside>
          </div>
        )}
      </AnimatePresence>

      {/* Mobile Header */}
      <header className="md:hidden flex items-center justify-between h-14 px-4 bg-surface-900/80 border-b border-surface-800/60 backdrop-blur-xl flex-shrink-0">
        <button
          onClick={() => setIsMobileMenuOpen(true)}
          className="p-2 -ml-2 text-surface-400 hover:text-surface-200 hover:bg-surface-800/50 rounded-lg transition-colors"
        >
          <Menu className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center overflow-hidden">
            <img src="/TY.png" alt="Tyaus Logo" className="w-full h-full object-contain" />
          </div>
          <span className="font-semibold text-sm text-white tracking-tight">Tyaus</span>
        </div>
        <button
          onClick={() => setIsOperationsPanelOpen(true)}
          className="p-2 -mr-2 text-surface-400 hover:text-surface-200 hover:bg-surface-800/50 rounded-lg transition-colors relative"
        >
          <Activity className="w-5 h-5" />
          {activeOperationsCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 bg-blue-500 text-white text-[10px] font-bold rounded-full">
              {activeOperationsCount}
            </span>
          )}
        </button>
      </header>

      {/* Main content - Scrollable */}
      <main className="flex-1 overflow-y-auto">
        <div className="p-4 md:p-8">
          <Outlet />
        </div>
      </main>

      {/* Operations Panel */}
      <OperationsPanel
        isOpen={isOperationsPanelOpen}
        onClose={() => setIsOperationsPanelOpen(false)}
      />
    </div>
  );
}
